// @flow
import React from 'react';
import { Link } from 'react-router-dom';
import LabelInput from 'components/register/LabelInput';
import Button from 'components/common/Button';
import { isBrowser } from 'react-device-detect';

import './RegisterForm.scss';

// 연락처 자동 하이픈 추가 함수
const formatPhoneNumber = value => {
  // 숫자만 추출
  const numbers = value.replace(/[^\d]/g, '');

  // 길이에 따라 포맷팅
  if (numbers.length <= 2) {
    return numbers;
  }

  // 지역번호 패턴 확인 (02, 031, 032, 033, 041, 042, 043, 044, 051, 052, 053, 054, 055, 061, 062, 063, 064)
  if (numbers.startsWith('02')) {
    // 서울 지역번호 (02-XXX-XXXX 또는 02-XXXX-XXXX)
    if (numbers.length <= 2) {
      return numbers;
    }
    if (numbers.length <= 5) {
      return `${numbers.slice(0, 2)}-${numbers.slice(2)}`;
    }
    if (numbers.length <= 9) {
      return `${numbers.slice(0, 2)}-${numbers.slice(2, 5)}-${numbers.slice(5)}`;
    }
    if (numbers.length <= 10) {
      return `${numbers.slice(0, 2)}-${numbers.slice(2, 6)}-${numbers.slice(6)}`;
    }
    // 10자리 초과시 10자리까지만 사용
    return `${numbers.slice(0, 2)}-${numbers.slice(2, 6)}-${numbers.slice(6, 10)}`;
  }

  // 기타 지역번호 (03X, 04X, 05X, 06X - XXX-XXX-XXXX 또는 XXX-XXXX-XXXX)
  if (/^(03[1-9]|04[1-4]|05[1-5]|06[1-4])/.test(numbers)) {
    if (numbers.length <= 3) {
      return numbers;
    }
    if (numbers.length <= 6) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    }
    if (numbers.length <= 10) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6)}`;
    }
    if (numbers.length <= 11) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
    }
    // 11자리 초과시 11자리까지만 사용
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
  }

  // 휴대폰 번호 (010, 011, 016, 017, 018, 019)
  if (/^(010|011|016|017|018|019)/.test(numbers)) {
    if (numbers.length <= 3) {
      return numbers;
    }
    if (numbers.length <= 7) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    }
    if (numbers.length <= 11) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
    }
    // 11자리 초과시 11자리까지만 사용
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
  }

  // 기본 패턴 (알 수 없는 번호)
  if (numbers.length <= 3) {
    return numbers;
  }
  if (numbers.length <= 7) {
    return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
  }
  if (numbers.length <= 11) {
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
  }
  // 11자리 초과시 11자리까지만 사용
  return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
};

// 연락처 유효성 검증 함수
const isValidPhoneNumber = value => {
  // 숫자만 추출
  const numbers = value.replace(/[^\d]/g, '');
  
  // 최소 9자리 이상이어야 함 (02-123-4567 형태)
  if (numbers.length < 9) {
    return false;
  }
  
  // 휴대폰 번호는 11자리
  if (/^(010|011|016|017|018|019)/.test(numbers)) {
    return numbers.length === 11;
  }
  
  // 서울 지역번호는 9~10자리
  if (numbers.startsWith('02')) {
    return numbers.length >= 9 && numbers.length <= 10;
  }
  
  // 기타 지역번호는 10~11자리
  if (/^(03[1-9]|04[1-4]|05[1-5]|06[1-4])/.test(numbers)) {
    return numbers.length >= 10 && numbers.length <= 11;
  }
  
  // 기본적으로 9~11자리 허용
  return numbers.length >= 9 && numbers.length <= 11;
};

// 사업자등록번호 자동 하이픈 추가 함수 (xxx-xx-xxxxx)
const formatBusinessNumber = value => {
  // 숫자만 추출
  const numbers = value.replace(/[^\d]/g, '');

  // 길이에 따라 포맷팅
  if (numbers.length <= 3) {
    return numbers;
  }
  if (numbers.length <= 5) {
    return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
  }
  if (numbers.length <= 10) {
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 5)}-${numbers.slice(5)}`;
  }
  // 10자리 초과시 10자리까지만 사용
  return `${numbers.slice(0, 3)}-${numbers.slice(3, 5)}-${numbers.slice(5, 10)}`;
};

type Props = {
  onChange(e: SyntheticInputEvent<HTMLInputElement>): void,
  onCheckBoxChange(e: SyntheticInputEvent<HTMLInputElement>): void,
  onAllCheckBox(e: SyntheticInputEvent<HTMLInputElement>): void,
  onRegister(): Promise<*>,
  managerName: string,
  displayName: string,
  email: string,
  password: string,
  mobile: string,
  bizNumber: string,
  shortBio: string,
  isAllAgree: boolean,
  isAdult: boolean,
  isTerms: boolean,
  isPrivacy: boolean,
  isPromotion: boolean,
  emailEditable: boolean,
  error: ?{
    name: string,
    payload: any,
  },
  hideEmail: boolean,
};

const errorMap = {
  DUPLICATED_ACCOUNT: (payload: string) =>
    `이미 존재하는 ${payload === 'email' ? '이메일' : '아이디'}입니다.`,
  FIELD_RULE: (payload: string) => {
    const rules = {
      email: '이메일은 소문자만 입력하세요.',
      password: '비밀번호는 6~30자로 입력하세요.',
      managerName: '이름을 1~20자로 입력하세요.',
      mobile: '올바른 연락처를 입력해주세요. (예: 010-1234-5678)',
      isAdult: '만 14세 이상이여야 합니다.',
      isTerms: '이용약관 동의 체크해주세요.',
      isPrivacy: '개인정보처리방침 동의 체크해주세요.',
    };
    return rules[payload] || '입력한 데이터가 문제가 있습니다.';
  },
};

const printError = (error: { name: string, payload: any }) => {
  if (!errorMap[error.name]) {
    return '회원가입이 실패하였습니다. 고객센터(02-585-1342)에 문의 해주시기 바랍니다.';
  }
  return errorMap[error.name](error.payload);
};

const RegisterForm = ({
  onChange,
  onCheckBoxChange,
  onAllAgree,
  onAllAgreeBtn,
  onRegister,
  managerName,
  displayName,
  email,
  password,
  mobile,
  bizNumber,
  isAllAgree,
  isAdult,
  isTerms,
  isPrivacy,
  isPromotion,
  error,
}: Props) => {
  return (
    <div className="register-form">
      <div className="form-head">
        <h2>회원가입</h2>
      </div>
      <div className="form-contents">
        <LabelInput
          type="email"
          value={email}
          name="email"
          required
          label="이메일"
          placeholder="이메일을 입력해주세요."
          onChange={onChange}
        />
        <LabelInput
          type="password"
          value={password}
          name="password"
          required
          label="비밀번호"
          placeholder="6자 이상 입력해주세요."
          onChange={onChange}
        />
        <LabelInput
          value={mobile}
          name="mobile"
          required
          label="연락처"
          placeholder="연락처를 입력해주세요."
          onChange={e => {
            const formattedValue = formatPhoneNumber(e.target.value);
            const syntheticEvent = {
              target: {
                name: e.target.name,
                value: formattedValue,
              },
            };
            onChange(syntheticEvent);
          }}
        />
        <LabelInput
          value={managerName}
          name="managerName"
          required
          label="이름"
          placeholder="이름을 입력해주세요."
          onChange={onChange}
        />
        <LabelInput
          value={displayName}
          name="displayName"
          label="상호/법인명/대학교명"
          placeholder="주식회사 데이터씨 or 서울대학교"
          onChange={onChange}
        />
        <LabelInput
          value={bizNumber}
          name="bizNumber"
          label="사업자등록번호"
          placeholder="사업자등록번호를 입력해주세요."
          onChange={e => {
            const formattedValue = formatBusinessNumber(e.target.value);
            const syntheticEvent = {
              target: {
                name: e.target.name,
                value: formattedValue,
              },
            };
            onChange(syntheticEvent);
          }}
        />
        <div className="register-form-group">
          <div className="register-form-group-label">약관 동의</div>
          <div className="register-form-group-input">
            <div className="register-terms-agree">
              <div className="register-terms-agree-all">
                <div className="form-check-checkbox-input">
                  <div className="form-check-label" onClick={onAllAgreeBtn}>
                    <input
                      type="checkbox"
                      name="isAllAgree"
                      checked={isAllAgree}
                      onChange={onAllAgree}
                      className="check-img"
                    />
                    <span className="register-terms-agree-all-text">전체동의</span>
                  </div>
                </div>
              </div>
              <div className="register-terms-agree-list">
                <div className="register-terms-agree-row">
                  <div className="form-check-checkbox-input">
                    <div className="form-check-label">
                      <input
                        type="checkbox"
                        name="isAdult"
                        checked={isAdult}
                        onChange={onCheckBoxChange}
                        className="check-img"
                      />
                      <span className="register-terms-agree-text">
                        만 14세 이상입니다.
                        <span className="register-terms-agree-required">(필수)</span>
                      </span>
                    </div>
                  </div>
                </div>
                <div className="register-terms-agree-row">
                  <div className="form-check-checkbox-input">
                    <div className="form-check-label">
                      <input
                        type="checkbox"
                        name="isTerms"
                        checked={isTerms}
                        onChange={onCheckBoxChange}
                        className="check-img"
                      />
                      <span className="register-terms-agree-text">
                        <a
                          className="register-terms-agree-link"
                          href="/policy/terms"
                          target="_blank"
                        >
                          캐시바이 이용약관
                        </a>
                        <span className="register-terms-agree-required">(필수)</span>
                      </span>
                    </div>
                  </div>
                </div>
                <div className="register-terms-agree-row">
                  <div className="form-check-checkbox-input">
                    <div className="form-check-label">
                      <input
                        type="checkbox"
                        name="isPrivacy"
                        checked={isPrivacy}
                        onChange={onCheckBoxChange}
                        className="check-img"
                      />
                      <span className="register-terms-agree-text">
                        <a
                          className="register-terms-agree-link"
                          href="/policy/privacy"
                          target="_blank"
                        >
                          개인정보 수집 및 이용
                        </a>
                        <span className="register-terms-agree-required">(필수)</span>
                      </span>
                    </div>
                  </div>
                </div>
                <div className="register-terms-agree-row">
                  <div className="form-check-checkbox-input">
                    <div className="form-check-label">
                      <input
                        type="checkbox"
                        name="isPromotion"
                        checked={isPromotion}
                        onChange={onCheckBoxChange}
                        className="check-img"
                      />
                      <span className="register-terms-agree-text">
                        개인정보 활용 및 마케팅 정보 수신 동의
                        <span className="register-terms-agree-chose">(선택)</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {error && <div className="error">{printError(error)}</div>}
        <Button
          fullWidth
          large
          theme="default"
          color="blue"
          onClick={onRegister}
          desktop={isBrowser}
        >
          회원가입 완료
        </Button>
        <div className="guideToLogin">
          <span>이미 아이디가 있으신가요?</span>
          <Link to={'/login'}>로그인</Link>
        </div>
      </div>
    </div>
  );
};

export default RegisterForm;



// WEBPACK FOOTER //
// ./src/components/register/RegisterForm/RegisterForm.js